import React, { useContext, useEffect, useRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { Search, Download, Sparkles, FileText, Heart, X } from "lucide-react";
import axios from "axios";
import "../../../assets/css/gcstudent/savednotespage/SavedNotesPage.css";
import AppContext from "../../../context/AppContext";
import Pagination from "../../../components/general/pagination/Pagination";
import Loader from "../../../components/general/loader/Loader";

/* ══════════════════════════════════════
   Delete Confirm Modal (heart click)
══════════════════════════════════════ */
const DeleteConfirmModal = ({ note, onClose, onConfirm, loading }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const displayNote = note.note || note;

  return (
    <div
      className="gcstudent-aimodal__backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="gcstudent-aimodal__dialog gcstudent-delete-modal">

        {/* Heart icon */}
        <div className="gcstudent-delete-modal__icon">
          <Heart size={32} fill="#e05c5c" color="#e05c5c" />
        </div>

        <h5 className="gcstudent-delete-modal__title">Remove from Saved?</h5>
        <p className="gcstudent-delete-modal__msg">
          Are you sure you want to remove{" "}
          <strong>"{displayNote.title}"</strong> from your saved notes?
        </p>

        <div className="gcstudent-delete-modal__actions">
          <button
            className="gcstudent-delete-modal__btn-cancel"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="gcstudent-delete-modal__btn-confirm"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Removing..." : "Yes, Remove"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════
   Saved Notes Page
══════════════════════════════════════ */
const ITEMS_PER_PAGE = 8; // yahan se page size control hoti hai (2-column grid, isliye 8)

const SavedNotesPage = () => {
  const { baseUrl, baseUrlForFiles, loggedInUser, authToken } = useContext(AppContext);

  const [searchQuery,   setSearchQuery]   = useState("");
  const [notes,         setNotes]         = useState([]);
  const [loading,       setLoading]       = useState(true);   // initial API fetch loading
  const [viewLoading,   setViewLoading]   = useState(false);  // page switch / search loading (sirf visible cards k liye)
  const [activeAINote,  setActiveAINote]  = useState(null);
  const [deleteTarget,  setDeleteTarget]  = useState(null); // note to delete
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [currentPage,   setCurrentPage]   = useState(1); // pagination state

  // Pehli render (initial mount) par view-loading trigger na ho, is liye ref se track kar rahe hain
  const isFirstRender = useRef(true);

  // ── Fetch saved notes — ab loggedInUser Context se aata hai ──
  const loadSavedNotes = () => {
    if (!loggedInUser?.id) return;

    setLoading(true);
    axios
      .get(`${baseUrl}/student/notes/getSavedNotes/${loggedInUser.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setLoading(false);
        console.log("Saved notes:", res.data);
        if (res.data.success === true) {
          setNotes(res.data.notes || []);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error("Load saved notes error:", err);
      });
  };

  useEffect(() => {
    if (loggedInUser?.id) {
      loadSavedNotes();
    }
  // eslint-disable-next-line
  }, [loggedInUser]);

  // ── Delete (unsave) ──
  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);

    axios
      .delete(`${baseUrl}/student/notes/removeSavedNote/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setDeleteLoading(false);
        console.log("Remove saved note:", res.data);
        if (res.data.success === true) {
          setNotes((prev) => prev.filter((n) => n.id !== deleteTarget.id));
          setDeleteTarget(null);
        }
      })
      .catch((err) => {
        setDeleteLoading(false);
        console.error("Remove error:", err);
      });
  };

  // ── Format date ──
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  };

  // ── Filter ──
  const filtered = notes.filter((n) => {
    const noteData = n.note || n;
    return (
      noteData.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      noteData.subject?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // ── Pagination slice (sirf current page ka data grid mein jayega) ──
  const paginatedNotes = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // ── Search change hone par page 1 pe reset ──
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  /* ── Sirf visible cards ki loading dikhayein:
     - Jab search se list filter ho
     - Jab pagination se dusre page par jayen (e.g. page 1 k 8 records dikhane
       k baad page 2 pe jayen tu sirf 9-16 wale records ki loading chale)
     Poora grid ek sath "pop" nahi hoga. ── */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setViewLoading(true);
    const timer = setTimeout(() => setViewLoading(false), 400);
    return () => clearTimeout(timer);
  }, [currentPage, searchQuery]);

  return (
    <div className="gcstudent-savednotes">

      {/* ── Page Header ── */}
      <div className="gcstudent-savednotes__header">
        <h2>My Saved Notes</h2>
        <p>Notes you've saved from the public library for quick access.</p>
      </div>

      {/* ── Search Bar ── */}
      <div className="gcstudent-savednotes__search-row">
        <div className="gcstudent-savednotes__search-wrap">
          <input
            type="text"
            className="gcstudent-savednotes__search-input"
            placeholder="Search saved notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button className="gcstudent-savednotes__search-btn">
          <Search size={15} strokeWidth={2.2} />
        </button>
      </div>

      {/* ── Initial Loading (poora page pehli baar load ho raha hai) ── */}
      {loading && <Loader message="Saved notes loading, Please wait..." />}

      {/* ── Notes Grid ── */}
      {!loading && (
        <>
          <Row className="g-3 mt-1">
            {viewLoading ? (
              /* ── Page switch / search loading — sirf is section ki, baaki page affect nahi hota ── */
              <Col xs={12}>
                <Loader message="Loading saved notes..." />
              </Col>
            ) : (
              <>
                {paginatedNotes.map((item, i) => {
                  // item.note = actual note data (from with('note'))
                  const noteData = item.note || item;
                  const pdfUrl = noteData.pdf_file
                    ? `${baseUrlForFiles}/notes/${noteData.pdf_file}`
                    : null;

                  return (
                    <Col xs={12} md={6} key={item.id}>
                      <div className="gcstudent-note-card" style={{ "--card-index": i }}>

                        {/* Heart — opens delete modal */}
                        <button
                          className="gcstudent-note-card__heart"
                          onClick={() => setDeleteTarget(item)}
                          title="Remove from saved"
                        >
                          <Heart size={16} fill="#e05c5c" color="#e05c5c" />
                        </button>

                        {/* Top: icon + info */}
                        <div className="gcstudent-note-card__top">
                          <div className="gcstudent-note-card__icon-wrap">
                            <FileText size={19} strokeWidth={1.7} />
                          </div>
                          <div className="gcstudent-note-card__info">
                            <h6 className="gcstudent-note-card__title">{noteData.title}</h6>
                            <p className="gcstudent-note-card__meta">
                              {noteData.subject}
                              {noteData.category ? ` • ${noteData.category}` : ""}
                            </p>
                            <p className="gcstudent-note-card__author">
                              By {noteData.author_name || noteData.teacher?.name || "Staff"} • Saved: {formatDate(item.created_at)}
                            </p>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="gcstudent-note-card__actions">

                          {/* Download */}
                          {pdfUrl ? (
                            <a
                              href={pdfUrl}
                              download={noteData.title || "note"}
                              className="gcstudent-note-btn gcstudent-note-btn--download"
                            >
                              <Download size={13} strokeWidth={2.2} />
                              Download
                            </a>
                          ) : (
                            <button className="gcstudent-note-btn gcstudent-note-btn--download" disabled>
                              <Download size={13} strokeWidth={2.2} />
                              Download
                            </button>
                          )}

                         

                        </div>
                      </div>
                    </Col>
                  );
                })}

                {/* Empty state */}
                {filtered.length === 0 && (
                  <Col xs={12}>
                    <div className="gcstudent-savednotes__empty">
                      <FileText size={38} strokeWidth={1.2} />
                      <p>No saved notes found.</p>
                    </div>
                  </Col>
                )}
              </>
            )}
          </Row>

          {/* ── Pagination (view-loading k dauran bhi visible rehti hai) ── */}
          <Pagination
            currentPage={currentPage}
            totalItems={filtered.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteTarget && (
        <DeleteConfirmModal
          note={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleConfirmDelete}
          loading={deleteLoading}
        />
      )}
    </div>
  );
};

export default SavedNotesPage;